import { jsPDF } from "jspdf";

/**
 * Generates a PDF document with multiple QR code cards using jsPDF
 * @param {Array} customers - Array of customer objects with qr_code
 * @param {Array} regions - Array of region objects
 * @returns {Promise<void>}
 */
export const generateQRCardsPDF = async (customers, regions = []) => {
  try {

    // Filter customers that have QR codes
    const customersWithQR = customers.filter((c) => c.qr_code);

    if (customersWithQR.length === 0) {
      throw new Error("No customers with QR codes found");
    }

    // PDF settings - A4 size
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth(); // 210mm for A4
    const pageHeight = pdf.internal.pageSize.getHeight(); // 297mm for A4
    const margin = 10; // mm
    const cardsPerRow = 2; // Fixed: 2 cards per row
    const spacing = 8; // mm between cards
    // Calculate card width to fit exactly 2 cards per row
    const cardWidth = (pageWidth - 2 * margin - spacing) / cardsPerRow; // ~96mm
    const cardHeight = 58; // mm (fits village field)
    const spacingVertical = 8; // mm between rows

    // Calculate how many cards fit per column
    const cardsPerColumn = Math.floor((pageHeight - 2 * margin) / (cardHeight + spacingVertical));
    const cardsPerPage = cardsPerRow * cardsPerColumn;

    let cardIndex = 0;

    // Process each customer
    for (let i = 0; i < customersWithQR.length; i++) {
      const customer = customersWithQR[i];

      // If we need a new page
      if (cardIndex > 0 && cardIndex % cardsPerPage === 0) {
        pdf.addPage();
      }

      // Calculate position on current page
      const positionOnPage = cardIndex % cardsPerPage;
      const row = Math.floor(positionOnPage / cardsPerRow);
      const col = positionOnPage % cardsPerRow;

      // Calculate position
      const x = margin + col * (cardWidth + spacing);
      const y = margin + row * (cardHeight + spacingVertical);

      // Draw card
      await drawCard(pdf, customer, regions, x, y, cardWidth, cardHeight);

      cardIndex++;
    }

    // Save PDF
    const timestamp = new Date().toISOString().split("T")[0];
    pdf.save(`QR_Cards_${timestamp}.pdf`);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
};

/**
 * Draws a single QR card on the PDF
 * @param {Object} pdf - jsPDF instance
 * @param {Object} customer - Customer object
 * @param {Array} regions - Array of region objects
 * @param {number} x - X position in mm
 * @param {number} y - Y position in mm
 * @param {number} width - Card width in mm
 * @param {number} height - Card height in mm
 */
const drawCard = async (pdf, customer, regions, x, y, width, height) => {
  // Card border and background
  pdf.setDrawColor(229, 231, 235); // #e5e7eb
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(x, y, width, height, 2, 2, "FD");

  const qrWidth = width * 0.5; // Left half for QR
  const detailsWidth = width * 0.5; // Right half for details
  const qrHeight = height;

  // Draw QR code section background (gradient effect - light blue)
  pdf.setFillColor(239, 246, 255); // #eff6ff
  pdf.roundedRect(x, y, qrWidth, qrHeight, 2, 2, "F");

  // Add QR code image (centered vertically and horizontally in left section)
  try {
    const qrPadding = 3;
    const qrImageSize = Math.min(qrWidth, qrHeight) - 2 * qrPadding;
    const qrImageX = x + (qrWidth - qrImageSize) / 2;
    const qrImageY = y + (qrHeight - qrImageSize) / 2;
    const innerPadding = 2;

    // Add white background for QR code
    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect(qrImageX, qrImageY, qrImageSize, qrImageSize, 1, 1, "F");

    // Add QR code image
    pdf.addImage(
      customer.qr_code,
      "PNG",
      qrImageX + innerPadding,
      qrImageY + innerPadding,
      qrImageSize - 2 * innerPadding,
      qrImageSize - 2 * innerPadding
    );
  } catch (error) {
    console.error("Error adding QR code image:", error);
    // Draw placeholder if image fails
    pdf.setFontSize(8);
    pdf.setTextColor(156, 163, 175);
    pdf.text("QR Code", x + qrWidth / 2, y + qrHeight / 2, {
      align: "center",
    });
  }

  // Draw border between QR and details
  pdf.setDrawColor(229, 231, 235);
  pdf.line(x + qrWidth, y, x + qrWidth, y + height);

  // Draw customer details section
  const detailsX = x + qrWidth;
  const detailsY = y;
  const padding = 3;

  // Details section - start from top
  let currentY = detailsY + padding + 2;
  const lineHeight = 5.5;
  const fontSize = 10;

  pdf.setFontSize(fontSize);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(17, 24, 39);

  // Customer Name
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(55, 65, 81); // #374151
  pdf.text("Customer Name:", detailsX + padding, currentY);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(17, 24, 39);
  pdf.text(customer?.name || "N/A", detailsX + padding, currentY + lineHeight, {
    maxWidth: detailsWidth - padding * 2,
  });
  currentY += lineHeight * 2;

  // CIN
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(55, 65, 81); // #374151
  pdf.text("CIN:", detailsX + padding, currentY);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(17, 24, 39);
  pdf.text(customer?.cin || "N/A", detailsX + padding, currentY + lineHeight);
  currentY += lineHeight * 2;

  // Service Number
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(55, 65, 81);
  pdf.text("Service Number:", detailsX + padding, currentY);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(17, 24, 39);
  pdf.text(customer?.usn || "N/A", detailsX + padding, currentY + lineHeight);
  currentY += lineHeight * 2;

  // Village
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(55, 65, 81);
  pdf.text("Village:", detailsX + padding, currentY);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(17, 24, 39);
  pdf.text(customer?.village || "N/A", detailsX + padding, currentY + lineHeight, {
    maxWidth: detailsWidth - padding * 2,
  });
  currentY += lineHeight * 2;

  // Company + Phone footer (unlabeled)
  pdf.setFontSize(fontSize - 1);
  pdf.setFont("helvetica", "italic");
  pdf.setTextColor(107, 114, 128); // muted gray
  pdf.text("Valence Energies", detailsX + padding, currentY);
  pdf.text("9492272320", detailsX + padding, currentY + lineHeight);
};
